import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { frontloadConnect } from 'react-frontload';
import { PageSkillAggregation } from 'components/pages/PageTaxonomyAggregation';
import {
  userShortcutsUrlsSelector,
  userRolesSelector,
  isAuthSelector,
  userEmailSelector,
  userFVSSectionMemberSelector,
  userOktaIdSelector,
} from 'modules/user/selectors';
import {
  aggsPageContentSelector,
  contentLoadingSelector,
  skillContentListSelector,
  skipContentFrontloadRequestSelector,
} from 'modules/content/selectors';
import { fetchAggsPageContent, resetSkipContentFrontloadRequest } from 'modules/content/actions';
import { skillsHashSelector, skillsSlugIdMapSelector } from 'modules/skills/selectors';
import {
  skillFromParamsSelectorFactory,
  subskillFromParamsSelectorFactory,
  pageSkillAggsSkillSlugSelector,
  pageSkillAggsSubskillSlugSelector,
} from 'modules/router';
import { emptyObject, emptyArray, getPath } from 'utils';
import { Routes } from 'constants/index';
import { toggleLoginReload } from 'modules/layouts';

const mapActionCreators = (dispatch: Dispatch) => ({
  async fetchAggsPageContent(): Promise<void> {
    await dispatch(fetchAggsPageContent()).catch(() => dispatch(push(getPath(Routes.NOT_FOUND))));
  },

  toggleLoginReload(shouldReload: boolean): void {
    dispatch(toggleLoginReload(shouldReload));
  },
  resetSkipContentFrontloadRequest(): void {
    dispatch(resetSkipContentFrontloadRequest());
  },
});

const mapStateToProps = (initState: State.Root) => {
  const skillFromParamsSelector = skillFromParamsSelectorFactory(pageSkillAggsSkillSlugSelector);
  const subskillFromParamsSelector = subskillFromParamsSelectorFactory(pageSkillAggsSubskillSlugSelector);

  const subskillsFromParamsSelector = createSelector(
    [skillsHashSelector, skillsSlugIdMapSelector, pageSkillAggsSkillSlugSelector],
    (
      skillsHash: State.SkillsHash | null,
      skillsSlugIdMap: { [key: string]: string },
      slug: string | null
    ): State.Subskill[] => {
      if (!slug || !skillsHash || !skillsSlugIdMap) return emptyArray;
      const skillId: string = skillsSlugIdMap[slug];
      if (!skillId) return emptyArray;
      const subskills: State.SubskillsHash = skillsHash[skillId].subskills;
      return Object.values(subskills || emptyObject);
    }
  );

  const userHasShortcutSelector = createSelector(
    [userShortcutsUrlsSelector, pageSkillAggsSkillSlugSelector, pageSkillAggsSubskillSlugSelector],
    (userShortcutsUrls: string[], skillSlug: string, subskillSlug: string): boolean => {
      if (!userShortcutsUrls || !userShortcutsUrls.length || !skillSlug) return false;
      return userShortcutsUrls.includes(`${skillSlug}${subskillSlug}`);
    }
  );

  const userRoles: string[] | undefined = userRolesSelector(initState);
  const userRolesStr: string = userRoles ? userRoles.join() : '';

  return (state: State.Root) => {
    const skillSlug: string = pageSkillAggsSkillSlugSelector(state);
    const subskillSlug: string = pageSkillAggsSubskillSlugSelector(state);
    const isSubskillPage: boolean = Boolean(subskillSlug);

    const skill: State.Skill | null = skillFromParamsSelector(state);
    const subskill: State.Subskill | null = subskillFromParamsSelector(state);
    const { id: skillId, name: skillName = '', description: skillDescription = '' }: any = skill || emptyObject;
    const {
      id: subskillId,
      name: subskillName = '',
      description: subskillDescription = '',
    }: any = subskill || emptyObject;

    return {
      loading: contentLoadingSelector(state),
      isSubskillPage,
      skillSlug,
      skillId,
      skillName,
      skillDescription,
      subskillSlug,
      subskillId,
      subskillName,
      subskillDescription,
      subskills: subskillsFromParamsSelector(state),
      aggsPageContent: aggsPageContentSelector(state),
      hasShortcut: userHasShortcutSelector(state),
      userRolesStr,
      isAuth: isAuthSelector(state),
      topicContentList: skillContentListSelector(state),
      userEmail: userEmailSelector(state),
      fvsMembership: userFVSSectionMemberSelector(state),
      oktaId: userOktaIdSelector(state),
      skipContentFrontloadRequest: skipContentFrontloadRequestSelector(state),
    };
  };
};

const frontload = async (props: any) => {
  if (props.skipContentFrontloadRequest) return props.resetSkipContentFrontloadRequest();
  if (props.skillSlug) {
    return props.fetchAggsPageContent();
  }
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: any, newProps: any) => {
    return (
      prevProps.isSubskillPage !== newProps.isSubskillPage ||
      prevProps.skillSlug !== newProps.skillSlug ||
      prevProps.subskillSlug !== newProps.subskillSlug ||
      prevProps.isAuth !== newProps.isAuth
    );
  },
};

export const PageSkillAggregationContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageSkillAggregation));
